// CallingView — tryb dzwonienia: kolejka, timer, wynik rozmowy.
// Ported from the prototype calling.jsx: server data via oRPC + TanStack Query,
// shared fmt* helpers, and the leads.logCall mutation.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CALLABLE_STATUSES, CALL_OUTCOMES, statusMeta } from '@delvero/contracts';
import type { LeadWithEcon } from '@delvero/contracts';
import { orpc } from '../../lib/orpc';
import { fmtDur, fmtMin, fmtZl, relDate } from '../../lib/format';
import { useShell } from '../../shell';
import { Icon } from '../../components/Icon';
import { StatusBadge } from '../../components/Status';

type CallOutcomeId = (typeof CALL_OUTCOMES)[number]['id'];

const callablePriority = (status: LeadWithEcon['status']): number =>
  (CALLABLE_STATUSES as readonly string[]).indexOf(status);

export function CallingView() {
  const { activeAppId, openLead, callTarget, setCallTarget } = useShell();
  const qc = useQueryClient();

  const leadsQuery = useQuery({
    ...orpc.leads.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });
  const leads = leadsQuery.data;

  const queue: LeadWithEcon[] = (leads ?? [])
    .filter((l) => callablePriority(l.status) >= 0)
    .sort(
      (a, b) =>
        callablePriority(b.status) - callablePriority(a.status) ||
        new Date(a.lastActivityAt).getTime() - new Date(b.lastActivityAt).getTime(),
    );

  const [currentId, setCurrentId] = useState<string | null>(() => callTarget ?? null);
  const [sec, setSec] = useState(0);
  const [running, setRunning] = useState(false);
  const [ended, setEnded] = useState(false);
  const [outcome, setOutcome] = useState<CallOutcomeId | null>(null);
  const [noteText, setNoteText] = useState('');

  // On mount: consume the call target from the shell so a later visit starts fresh.
  useEffect(() => {
    if (callTarget) setCallTarget(null);
    // Mount-only: the initial callTarget is captured in useState above.
  }, []);

  // Timer: tick every second while running.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  // Resolve the current lead against the queue, defaulting to the head.
  const current = queue.find((l) => l.id === currentId) ?? queue[0] ?? null;
  const resolvedCurrentId = current?.id ?? null;
  useEffect(() => {
    if (resolvedCurrentId && resolvedCurrentId !== currentId) setCurrentId(resolvedCurrentId);
  }, [resolvedCurrentId, currentId]);

  // Detail (activities) for the current lead — for the context call/note history.
  const detailQuery = useQuery({
    ...orpc.leads.get.queryOptions({ input: { id: resolvedCurrentId ?? '' } }),
    enabled: !!resolvedCurrentId,
  });

  const resetCall = () => {
    setSec(0);
    setRunning(false);
    setEnded(false);
    setOutcome(null);
    setNoteText('');
  };

  const pick = (id: string) => {
    if (running) return;
    resetCall();
    setCurrentId(id);
  };

  const advanceFrom = (id: string) => {
    const idx = queue.findIndex((l) => l.id === id);
    const next = queue[idx + 1] ?? queue.find((l) => l.id !== id) ?? null;
    setCurrentId(next ? next.id : null);
  };

  const logCall = useMutation(
    orpc.leads.logCall.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: orpc.leads.key() });
        qc.invalidateQueries({ queryKey: orpc.dashboard.key() });
        qc.invalidateQueries({ queryKey: orpc.costs.key() });
      },
    }),
  );

  const saveCall = () => {
    if (!current || !outcome) return;
    const id = current.id;
    logCall.mutate({ id, durationSec: sec, outcome, note: noteText.trim() });
    resetCall();
    advanceFrom(id);
  };

  // Loading placeholder while the active app / leads resolve.
  if (!activeAppId || (leadsQuery.isLoading && !leads)) {
    return (
      <div className="view">
        <div className="card center-pad">
          <span className="spinner" /> Wczytywanie…
        </div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="view">
        <div className="card empty-state">
          <Icon name="phone" size={22} />
          <div className="empty-title">Kolejka pusta</div>
          <div className="cell-sub">
            Brak leadów ze statusem Nowy, Cold mail, Follow-up lub Call umówiony.
          </div>
        </div>
      </div>
    );
  }

  // Economics for the current lead come from the list row (LeadWithEcon).
  const econ = current?.econ ?? null;
  const planName = current?.planName ?? null;

  // Last up-to-3 call/note activities from the detail, newest first.
  const lastCalls = (detailQuery.data?.activities ?? [])
    .filter((a) => a.type === 'call' || a.type === 'note')
    .slice(-3)
    .reverse();

  return (
    <div className="view calling-grid">
      <div className="card queue-card">
        <div className="queue-head">
          Kolejka <span className="mono">{queue.length}</span>
        </div>
        {queue.map((l) => {
          const meta = statusMeta(l.status);
          return (
            <button
              key={l.id}
              className={'queue-item' + (current && l.id === current.id ? ' active' : '')}
              onClick={() => pick(l.id)}
              disabled={running}
            >
              <span className="dot" style={{ background: meta.color }}></span>
              <span className="queue-name">
                <span className="cell-main">{l.company}</span>
                <span className="cell-sub">
                  {meta.label} · {relDate(l.lastActivityAt)}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {current ? (
        <div className="card call-card">
          <div className="call-top">
            <div>
              <div className="call-company">{current.company}</div>
              <div className="cell-sub">
                {current.contact}
                {current.role ? ' · ' + current.role : ''} · zainteresowanie: {planName ?? '—'}
              </div>
            </div>
            <StatusBadge status={current.status} />
          </div>
          <div className="call-phone mono">{current.phone || 'brak numeru'}</div>

          <div className={'timer mono' + (running ? ' live' : '')}>{fmtDur(sec)}</div>

          {!ended ? (
            <div className="call-actions">
              {!running ? (
                <button
                  className="btn primary big"
                  onClick={() => {
                    setRunning(true);
                    setEnded(false);
                  }}
                >
                  {sec > 0 ? 'Wznów' : 'Start rozmowy'}
                </button>
              ) : (
                <button
                  className="btn danger big"
                  onClick={() => {
                    setRunning(false);
                    setEnded(true);
                  }}
                >
                  Zakończ rozmowę
                </button>
              )}
            </div>
          ) : (
            <div className="outcome-box">
              <div className="econ-title">Wynik rozmowy — {fmtDur(sec)}</div>
              <div className="outcome-grid">
                {CALL_OUTCOMES.map((o) => (
                  <button
                    key={o.id}
                    className={'chip outcome-chip' + (outcome === o.id ? ' chip-on' : '')}
                    onClick={() => setOutcome(o.id)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea
            className="input call-note"
            rows={3}
            placeholder="Szybka notatka z rozmowy…"
            value={noteText}
            onChange={(ev) => setNoteText(ev.target.value)}
          ></textarea>

          <div className="call-foot">
            <button className="btn" onClick={() => openLead(current.id)}>
              Karta klienta
            </button>
            <div className="call-foot-right">
              <button
                className="btn"
                onClick={() => {
                  const id = current.id;
                  resetCall();
                  advanceFrom(id);
                }}
                disabled={running}
              >
                Pomiń
              </button>
              <button
                className="btn primary"
                onClick={saveCall}
                disabled={!ended || !outcome || logCall.isPending}
              >
                Zapisz i następny
              </button>
            </div>
          </div>

          <div className="call-context">
            <div className="econ-title">Kontekst</div>
            <div className="econ-line">
              <span>Dotychczasowe calle</span>
              <span className="mono">
                {econ ? econ.callCount : 0} szt. · {fmtMin(econ ? econ.callSec : 0)}
              </span>
            </div>
            <div className="econ-line">
              <span>Koszt do tej pory</span>
              <span className="mono">{fmtZl(econ ? econ.cac : 0)}</span>
            </div>
            {lastCalls.map((a) => (
              <div className="ctx-note" key={a.id}>
                <span className="tl-date">
                  {relDate(a.at)}
                  {a.durationSec != null ? ' · ' + fmtDur(a.durationSec) : ''}
                </span>
                {a.note || a.outcome || '—'}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
